import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowLeftIcon, ArrowRightIcon } from "@/components/common/Icons";

/**
 * useCarousel Hook
 * Wrapper around Embla carousel with common functionality
 */
export function useCarousel(options = {}) {
  const defaultOptions = {
    align: "start",
    dragFree: true,
    loop: true,
    containScroll: false,
    ...options,
  };

  const [emblaRef, emblaApi] = useEmblaCarousel(defaultOptions);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback(
    (index) => emblaApi?.scrollTo(index),
    [emblaApi],
  );

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    const onInit = () => setScrollSnaps(emblaApi.scrollSnapList());

    emblaApi.on("select", onSelect);
    emblaApi.on("init", onInit);
    onInit();
    onSelect();

    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("init", onInit);
    };
  }, [emblaApi]);

  return {
    emblaRef,
    emblaApi,
    selectedIndex,
    scrollSnaps,
    scrollPrev,
    scrollNext,
    scrollTo,
  };
}

/**
 * CarouselNavButtons Component
 * Navigation buttons for carousel
 */
export function CarouselNavButtons({
  onPrev,
  onNext,
  variant = "default",
  className = "",
}) {
  const isUniversity = variant === "university";

  const containerStyles = `
    flex justify-between items-center
    rounded-[88rem] p-1.5 box-border
    mt-8 mx-auto w-fit gap-2
    ${isUniversity ? "bg-white/[0.07]" : "bg-black/[0.02]"}
    ${className}
  `;

  const buttonStyles = `
    flex justify-center items-center
    cursor-pointer rounded-full
    w-9 h-9 border-none
    transition-all duration-300
    active:scale-95
    ${isUniversity ? "bg-white/[0.09]" : "bg-white"}
  `;

  const iconColor = isUniversity ? "white" : "var(--color-main)";

  return (
    <div className={containerStyles}>
      <button
        onClick={onPrev}
        className={buttonStyles}
        aria-label="Previous slide"
      >
        <ArrowLeftIcon className="w-5 h-5" color={iconColor} />
      </button>
      <button onClick={onNext} className={buttonStyles} aria-label="Next slide">
        <ArrowRightIcon className="w-5 h-5" color={iconColor} />
      </button>
    </div>
  );
}

/**
 * CarouselDots Component
 * Dot pagination for carousel
 */
export function CarouselDots({
  count,
  selectedIndex,
  onSelect,
  className = "",
}) {
  return (
    <div className={`flex justify-center gap-2 mt-8 ${className}`}>
      {Array.from({ length: count }).map((_, index) => (
        <button
          key={index}
          onClick={() => onSelect(index)}
          className={`
            w-2 h-2 rounded-full border-none cursor-pointer
            transition-all hover:-translate-y-0.5 active:translate-y-0
            ${index === selectedIndex ? "bg-main" : "bg-light-gray"}
          `}
          aria-label={`Go to slide ${index + 1}`}
        />
      ))}
    </div>
  );
}

/**
 * Carousel Component
 * Main carousel wrapper
 */
export function Carousel({
  children,
  options = {},
  showNav = true,
  showDots = false,
  navVariant = "default",
  className = "",
  slideClassName = "",
  containerClassName = "",
}) {
  const {
    emblaRef,
    selectedIndex,
    scrollSnaps,
    scrollPrev,
    scrollNext,
    scrollTo,
  } = useCarousel(options);

  return (
    <div className={`embla ${className}`}>
      <div ref={emblaRef} className="embla__viewport">
        <div className={`embla__container ${containerClassName}`}>
          {children}
        </div>
      </div>

      {showNav && (
        <CarouselNavButtons
          onPrev={scrollPrev}
          onNext={scrollNext}
          variant={navVariant}
        />
      )}

      {showDots && scrollSnaps.length > 0 && (
        <CarouselDots
          count={scrollSnaps.length}
          selectedIndex={selectedIndex}
          onSelect={scrollTo}
        />
      )}
    </div>
  );
}

export default Carousel;
